import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Send, Trash2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuthStore } from "@/features/auth/store";

type Discussion = {
  commentId: number;
  content: string | null;
  createDate: string | null;
  createBy: number | null;
  createByLogin: string | null;
  createByName: string | null;
};

async function fetchDiscussions(textId: number): Promise<Discussion[]> {
  const { data } = await api.get(`/text-sign/${textId}/discussions`);
  return data.data ?? data;
}

async function postDiscussion(textId: number, content: string) {
  const { data } = await api.post(`/text-sign/${textId}/discussions`, { content });
  return data.data ?? data;
}

async function deleteDiscussion(commentId: number) {
  const { data } = await api.post(`/text-sign/discussions/${commentId}/delete`);
  return data.data ?? data;
}

export function TextSignDiscussions({ textId }: { textId: number }) {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["text-sign", textId, "discussions"],
    queryFn: () => fetchDiscussions(textId),
    refetchInterval: 30_000, // auto refresh 30s
  });

  const add = useMutation({
    mutationFn: () => postDiscussion(textId, draft),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: ["text-sign", textId, "discussions"] });
    },
  });

  const remove = useMutation({
    mutationFn: (commentId: number) => deleteDiscussion(commentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["text-sign", textId, "discussions"] }),
  });

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle>
          <MessageSquare className="inline h-4 w-4 mr-1.5 -mt-0.5" /> Thảo luận văn bản
        </CardTitle>
        <span className="text-xxs uppercase tracking-wider text-muted-foreground">
          {data?.length ?? 0} ý kiến
        </span>
      </CardHeader>
      <CardBody className="p-0">
        <div className="max-h-[480px] overflow-y-auto">
          {isLoading && (
            <div className="p-6 text-center text-muted-foreground text-sm">Đang tải…</div>
          )}
          {!isLoading && (!data || data.length === 0) && (
            <div className="p-6 text-center text-muted-foreground text-sm">
              Chưa có ý kiến nào. Hãy bắt đầu thảo luận.
            </div>
          )}
          <ul className="divide-y divide-border">
            {data?.map((d) => {
              const mine = d.createBy === me?.staffId;
              const initials = (d.createByName ?? d.createByLogin ?? "?")
                .split(/\s+/)
                .slice(-2)
                .map((w) => w[0])
                .join("")
                .toUpperCase();
              return (
                <li key={d.commentId} className="px-5 py-3 flex gap-3">
                  <div
                    className={`h-9 w-9 shrink-0 rounded-md grid place-items-center text-xs font-semibold ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}
                  >
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-sm">
                        {d.createByName ?? d.createByLogin}
                      </span>
                      <span className="text-xxs text-muted-foreground tabular-nums">
                        {d.createDate}
                      </span>
                      {mine && (
                        <button
                          onClick={() => {
                            if (confirm("Xoá ý kiến này?")) remove.mutate(d.commentId);
                          }}
                          className="ml-auto text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-0.5"
                        >
                          <Trash2 className="h-3 w-3" /> Xoá
                        </button>
                      )}
                    </div>
                    <div className="text-sm whitespace-pre-wrap text-foreground">{d.content}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="border-t border-border p-3 flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Viết ý kiến của bạn… (Ctrl+Enter để gửi)"
            className="flex-1 resize-none min-h-[44px] max-h-32 px-3 py-2 rounded border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && draft.trim()) add.mutate();
            }}
          />
          <Button
            variant="default"
            size="md"
            onClick={() => add.mutate()}
            disabled={!draft.trim() || add.isPending}
          >
            <Send className="h-4 w-4 mr-1.5" />
            {add.isPending ? "Đang gửi…" : "Gửi"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
